import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Info } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { helpFor, isInternalPath, type HelpEntry } from "@/lib/help";
import { cn } from "@/lib/utils";

/** Long enough to cross the gap between the icon and the popover without it closing. */
const CLOSE_DELAY_MS = 120;

/**
 * The ⓘ beside a card or field title.
 *
 * - **Pointer:** hovering opens it, leaving closes it, pressing pins it open.
 * - **Keyboard:** it takes focus like a button, Enter or Space opens it, Escape closes it.
 * - **Touch:** there is no hover, so the icon is faintly visible all the time and a tap opens it.
 *
 * The icon is hidden until the nearest `group/card` is hovered or has focus inside it, so a
 * dense screen stays quiet. It is never `display:none` — a hidden control cannot take focus —
 * only transparent, and it takes full opacity the moment it is focused or open.
 *
 * ⚠️ **Plain text only.** Content comes from the typed registry in `lib/help.ts` and is drawn
 * as text nodes. Nothing here accepts markup.
 *
 * ⚠️ **Renders nothing for an id with no entry**, rather than an icon that opens an empty box.
 * `help.test.ts` is what turns a missing entry into a failure instead of a silent gap.
 */
export function InfoTip({ id, className }: { id: string; className?: string }) {
  const entry = helpFor(id);
  if (!entry) return null;
  return <InfoTipView id={id} entry={entry} className={className} />;
}

function InfoTipView({
  id,
  entry,
  className,
}: {
  id: string;
  entry: HelpEntry;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const descId = `${id}-help`;

  const cancelClose = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  React.useEffect(() => cancelClose, []);

  const hoverOpen = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    cancelClose();
    setOpen(true);
  };
  const hoverClose = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse" || pinned) return;
    cancelClose();
    timer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Escape, an outside press, or a tap on touch. All of them end the pin.
        setOpen(next);
        if (!next) setPinned(false);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="What is this?"
          aria-describedby={open ? descId : undefined}
          onPointerEnter={hoverOpen}
          onPointerLeave={hoverClose}
          onClick={(e) => {
            // With a mouse the hover has already opened it; a press means "keep it".
            if (open && !pinned && (e.nativeEvent as PointerEvent).pointerType === "mouse") {
              e.preventDefault();
              setPinned(true);
            }
          }}
          className={cn(
            "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground",
            "transition-opacity hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
            // Hidden until the card is engaged; always visible once focused or open.
            "opacity-0 group-hover/card:opacity-100 group-focus-within/card:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
            // No hover on touch: leave it faintly visible so the help is reachable at all.
            "[@media(hover:none)]:opacity-60",
            className,
          )}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={descId}
        side="bottom"
        align="start"
        className="w-80 space-y-2 p-3 text-xs leading-relaxed"
        onPointerEnter={hoverOpen}
        onPointerLeave={hoverClose}
        // The trigger keeps focus; a popover that steals it would move the reader's place.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <p className="font-medium text-foreground">{entry.what}</p>
        {entry.how && <p className="text-muted-foreground">{entry.how}</p>}
        {entry.learnMore && isInternalPath(entry.learnMore) && (
          <Link
            to={entry.learnMore}
            className="inline-block font-medium text-primary underline-offset-2 hover:underline"
          >
            Learn more
          </Link>
        )}
      </PopoverContent>
    </Popover>
  );
}
